import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Fade,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  TextField,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { H1, H4, Paragraph, Small } from "components/Typography";
import { colors } from "common";
import NothingToShow from "components/worth-it-builder/NothingToShow";
import { RmaInfoCard } from "components/card/rma";
import useResolution from "hooks/useResolution";
import modules from "modules";
import { useAppDispatch } from "hooks/use-app-dispatch";
import { useAppSelector } from "hooks/use-app-selector";
import { get } from "http";
import { useRouter } from "next/router";
import { set } from "nprogress";
import { useLoginStatus } from "hooks/useLoginStatus";
import { useDialogueShow } from "hooks/useDialogue";
import WestIcon from "@mui/icons-material/West";
import AlertDialog from "components/dialog/AlertDialog";
import { is } from "date-fns/locale";
import ClearIcon from "@mui/icons-material/Clear";

const { actions: userRmaActions, selectors: userRmaSelectors } =
  modules.pcWorthCare;

const PCWCare = () => {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const { isMobile } = useResolution();
  const { sn, tn } = router.query;

  const [serialSearch, setSerialSearch] = useState<string>("");
  const [transactionNoSearch, setTransactionNoSearch] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const { userRma, isUserRmaDetailsLoading } = useAppSelector(
    (state) => state.PcWorthCare
  );

  const getUserRmaDetails = useCallback(
    (
      serial: string | string[] | undefined,
      trnNo: string | string[] | undefined
    ) => {
      setIsLoading(true);

      const normalizedSerial = Array.isArray(serial) ? serial[0] : serial;
      const normalizedtrnNo = Array.isArray(trnNo) ? trnNo[0] : trnNo;

      setSerialSearch(normalizedSerial || "");
      setTransactionNoSearch(normalizedtrnNo || "");

      dispatch(
        userRmaActions.getUserRmaDetailsThunk({
          serial_no: normalizedSerial || "",
          transaction_no: normalizedtrnNo || "",
        })
      )
        .unwrap()
        .finally(() => {
          setIsLoading(false);
        });
    },
    [dispatch]
  );

  const onClickSearch = () => {
    getUserRmaDetails(serialSearch, transactionNoSearch);
  };

  useEffect(() => {
    if (!router.isReady) {
      setIsLoading(false);
      return;
    }

    const hasSn = typeof sn === "string" && sn.trim() !== "";
    const hasTn = typeof tn === "string" && tn.trim() !== "";

    if (hasSn || hasTn) {
      getUserRmaDetails(sn, tn);
    }
  }, [getUserRmaDetails, router.isReady, sn, tn]);

  return (
    <div title="PC Worth CARES">
      <Fade in={isLoading && isUserRmaDetailsLoading}>
        <LinearProgress />
      </Fade>

      <Container maxWidth="xl" sx={{ mt: 3, mb: 3 }}>
        <Box
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 20,
          }}
        >
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              window.location.href = "/";
            }}
            style={{
              alignSelf: "flex-start",
              padding: isMobile ? "5px 10px" : null,
            }}
          >
            {isMobile ? <WestIcon fontSize="small" /> : "Home"}
          </Button>

          <div
            style={{
              textAlign: "center",
              flexGrow: 1,
              marginLeft: isMobile ? 25 : 0,
            }}
          >
            <H1>
              <span style={{ color: "#0c304a" }}>PC </span>
              <span style={{ color: "#cf151a" }}>Worth</span> Cares
            </H1>

            <H4>
              <span style={{ textDecoration: "underline" }}>
                Return Merchandise Authorization
              </span>
            </H4>
          </div>

          <div style={{ width: 75 }} />
        </Box>

        <Card
          style={{
            marginTop: 10,
            width: isMobile ? "100%" : "40%",
            marginLeft: "auto",
            marginRight: "auto",
            backgroundColor: isMobile ? undefined : "transparent",
            boxShadow: isMobile ? undefined : "none",
          }}
        >
          <CardContent style={{ marginBottom: -6 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Search Serial No."
                  fullWidth
                  value={serialSearch}
                  onChange={(e) => setSerialSearch(e.target.value)}
                  InputProps={{
                    endAdornment: serialSearch && (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setSerialSearch("")}
                          edge="end"
                        >
                          <ClearIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Search Transaction No."
                  fullWidth
                  value={transactionNoSearch}
                  onChange={(e) => setTransactionNoSearch(e.target.value)}
                  InputProps={{
                    endAdornment: transactionNoSearch && (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setTransactionNoSearch("")}
                          edge="end"
                        >
                          <ClearIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item sm={12}>
                <Button
                  color="secondary"
                  fullWidth
                  variant="contained"
                  onClick={onClickSearch}
                >
                  Search
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Box
          mt={5}
          maxHeight={550}
          style={{ overflowY: "auto" }}
          paddingRight={1}
        >
          {userRma && userRma.length > 0 ? (
            userRma.map((order, index) => (
              <RmaInfoCard key={index} rma={order} />
            ))
          ) : (
            <NothingToShow
              image="/assets/images/kachi/kachi-happy.png"
              title="Looks like all your products are working perfectly! ✨✨✨"
              subtitle="📋 If you ever run into an issue, we’ve got your back. You can file an RMA anytime! 📋"
            />
          )}
        </Box>
      </Container>
    </div>
  );
};

export default PCWCare;
